import React, { useState, useEffect } from "react";
import { Card, Row, Col, Tag, Button, Space, Divider, Typography, Tooltip, Alert, Spin } from "antd";
import { ClockCircleOutlined, ArrowRightOutlined, InfoCircleOutlined, WarningOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import { getFlightComparison } from "../../api/flightOperationApi";

const { Title, Text } = Typography;

dayjs.extend(utc);

/**
 * 航班变更对比展示组件
 * 展示航班从基线版本到当前版本的变更情况
 */
const FlightComparisonViewer = ({ flightId, visible, onClose }) => {
    const [loading, setLoading] = useState(false);
    const [comparison, setComparison] = useState(null);
    const [error, setError] = useState(null);

    // 获取航班对比数据
    const fetchComparison = async () => {
        if (!flightId) return;

        setLoading(true);
        setError(null);

        try {
            const response = await getFlightComparison(flightId);
            if (response?.data?.success) {
                setComparison(response.data.data);
            } else {
                setError("获取航班对比数据失败");
            }
        } catch (err) {
            console.error("获取航班对比数据失败:", err);
            setError(err.response?.data?.detail || "获取航班对比数据失败");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (visible && flightId) {
            fetchComparison();
        }
    }, [visible, flightId]);

    // 格式化时间显示
    const formatTime = (time) => {
        if (!time) return "未设置";
        return dayjs.utc(time).format("MM-DD HH:mm");
    };

    // 格式化完整时间显示
    const formatFullTime = (time) => {
        if (!time) return "未设置";
        return dayjs.utc(time).format("YYYY-MM-DD HH:mm:ss");
    };

    // 获取变更类型的颜色和图标
    const getChangeTypeStyle = (changeType) => {
        switch (changeType) {
            case "delayed":
                return { color: "#ff4d4f", icon: <WarningOutlined /> };
            case "advanced":
                return { color: "#52c41a", icon: <ClockCircleOutlined /> };
            case "modified":
                return { color: "#1890ff", icon: <InfoCircleOutlined /> };
            default:
                return { color: "#8c8c8c", icon: <InfoCircleOutlined /> };
        }
    };

    // 渲染单个字段的变更对比
    const renderFieldComparison = (fieldKey, changeInfo) => {
        const { field_name, baseline_value, current_value, change_type, change_description, time_diff_minutes, field_type } = changeInfo;
        const style = getChangeTypeStyle(change_type);

        // 根据字段类型决定如何显示值
        const formatValue = (value, fieldKey, fieldType) => {
            if (!value) return "未设置";

            // 如果是时间类型字段，使用时间格式化
            if (fieldType === "datetime" || fieldKey.includes("time") || fieldKey.includes("td") || fieldKey.includes("ta")) {
                return formatTime(value);
            }

            // 如果是状态字段，显示中文翻译
            if (fieldKey === "status") {
                const statusMap = {
                    SCHEDULED: "已计划",
                    ACTIVE: "执行中",
                    COMPLETED: "已完成",
                    CANCELLED: "已取消",
                    DELAYED: "延误",
                    DIVERTED: "备降",
                    REPLACED: "已替换",
                    PLAN_CANCELLED: "计划性取消",
                };
                return statusMap[value] || value;
            }

            // 其他类型直接显示
            return value;
        };

        // 获取状态字段的Tag颜色
        const getStatusTagColor = (status) => {
            const colorMap = {
                SCHEDULED: "blue",
                ACTIVE: "processing",
                COMPLETED: "success",
                CANCELLED: "error",
                DELAYED: "warning",
                DIVERTED: "warning",
                REPLACED: "default",
                PLAN_CANCELLED: "error",
            };
            return colorMap[status] || "default";
        };

        return (
            <Card key={fieldKey} size="small" style={{ marginBottom: 8 }}>
                <Row align="middle" gutter={16}>
                    <Col flex="120px">
                        <Text strong>{field_name}</Text>
                    </Col>
                    <Col flex="auto">
                        <Space align="center">
                            {fieldKey === "status" ? <Tag color={getStatusTagColor(baseline_value)}>{formatValue(baseline_value, fieldKey, field_type)}</Tag> : <Text type="secondary">{formatValue(baseline_value, fieldKey, field_type)}</Text>}
                            <ArrowRightOutlined style={{ color: style.color }} />
                            {fieldKey === "status" ? (
                                <Tag color={getStatusTagColor(current_value)}>{formatValue(current_value, fieldKey, field_type)}</Tag>
                            ) : (
                                <Text strong style={{ color: style.color }}>
                                    {formatValue(current_value, fieldKey, field_type)}
                                </Text>
                            )}
                        </Space>
                    </Col>
                    <Col flex="200px">
                        <Space>
                            {style.icon}
                            <Text style={{ color: style.color }}>{change_description}</Text>
                            {time_diff_minutes !== undefined && Math.abs(time_diff_minutes) >= 60 && <Tag color={time_diff_minutes > 0 ? "red" : "green"}>{Math.abs(Math.round(time_diff_minutes / 60))}小时</Tag>}
                        </Space>
                    </Col>
                </Row>
            </Card>
        );
    };

    // 渲染航班基本信息对比
    const renderFlightInfoComparison = () => {
        if (!comparison) return null;

        const { baseline, current } = comparison;

        return (
            <Card title="航班基本信息对比" size="small" style={{ marginBottom: 16 }}>
                <Row gutter={16}>
                    <Col span={12}>
                        <Card size="small" title="基线版本（最初计划）" style={{ height: "100%" }}>
                            <Space direction="vertical" size="small" style={{ width: "100%" }}>
                                <div>
                                    <Text strong>航班号:</Text> {baseline.flight_number}
                                </div>
                                <div>
                                    <Text strong>航线:</Text> {baseline.departure_airport} → {baseline.arrival_airport}
                                </div>
                                <div>
                                    <Text strong>计划起飞:</Text> {formatFullTime(baseline.std)}
                                </div>
                                <div>
                                    <Text strong>计划到达:</Text> {formatFullTime(baseline.sta)}
                                </div>
                                <div>
                                    <Text strong>飞机:</Text> {baseline.aircraft_reg || "未分配"}
                                </div>
                                <div>
                                    <Text strong>快照时间:</Text> {formatFullTime(baseline.snapshot_time)}
                                </div>
                            </Space>
                        </Card>
                    </Col>
                    <Col span={12}>
                        <Card size="small" title="当前版本" style={{ height: "100%" }}>
                            <Space direction="vertical" size="small" style={{ width: "100%" }}>
                                <div>
                                    <Text strong>航班号:</Text> {current.flight_number}
                                </div>
                                <div>
                                    <Text strong>航线:</Text> {current.departure_airport} → {current.arrival_airport}
                                </div>
                                <div>
                                    <Text strong>计划起飞:</Text> {formatFullTime(current.std)}
                                </div>
                                <div>
                                    <Text strong>计划到达:</Text> {formatFullTime(current.sta)}
                                </div>
                                <div>
                                    <Text strong>飞机:</Text> {current.aircraft_reg || "未分配"}
                                </div>
                                <div>
                                    <Text strong>快照时间:</Text> {formatFullTime(current.snapshot_time)}
                                </div>
                            </Space>
                        </Card>
                    </Col>
                </Row>
            </Card>
        );
    };

    // 渲染变更详情
    const renderChanges = () => {
        if (!comparison?.changes || Object.keys(comparison.changes).length === 0) {
            return <Alert message="当前版本即为基线版本" description="该航班相比最初计划未发生任何变更。" type="info" showIcon />;
        }

        // 按重要性排序变更字段
        const sortedChanges = Object.entries(comparison.changes).sort(([keyA], [keyB]) => {
            const priorityOrder = ["std", "sta", "etd", "eta", "atd", "ata", "status", "aircraft_reg", "aircraft_type"];
            const indexA = priorityOrder.indexOf(keyA);
            const indexB = priorityOrder.indexOf(keyB);

            if (indexA !== -1 && indexB !== -1) {
                return indexA - indexB;
            }
            if (indexA !== -1) return -1;
            if (indexB !== -1) return 1;
            return keyA.localeCompare(keyB);
        });

        return (
            <Card title="变更详情" size="small">
                <Space direction="vertical" size="small" style={{ width: "100%" }}>
                    {sortedChanges.map(([fieldKey, changeInfo]) => renderFieldComparison(fieldKey, changeInfo))}
                </Space>
            </Card>
        );
    };

    // 计算总体延误情况
    const calculateOverallDelay = () => {
        if (!comparison?.changes) return null;

        const stdChange = comparison.changes.std;
        const staChange = comparison.changes.sta;

        if (stdChange?.time_diff_minutes || staChange?.time_diff_minutes) {
            const stdDelay = stdChange?.time_diff_minutes || 0;
            const staDelay = staChange?.time_diff_minutes || 0;
            const avgDelay = (stdDelay + staDelay) / 2;

            if (avgDelay > 0) {
                return {
                    type: "delayed",
                    minutes: Math.round(avgDelay),
                    description: `平均延误 ${Math.round(avgDelay)} 分钟`,
                };
            } else if (avgDelay < 0) {
                return {
                    type: "advanced",
                    minutes: Math.round(Math.abs(avgDelay)),
                    description: `平均提前 ${Math.round(Math.abs(avgDelay))} 分钟`,
                };
            }
        }

        return null;
    };

    if (!visible) return null;

    const isComparingSameVersion = comparison && (!comparison.changes || Object.keys(comparison.changes).length === 0);

    return (
        <div style={{ padding: "16px", backgroundColor: "#f5f5f5", borderRadius: "8px" }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
                <Title level={4} style={{ margin: 0 }}>
                    航班变更对比分析
                    {isComparingSameVersion && (
                        <Tag color="green" style={{ marginLeft: 8 }}>
                            无变更
                        </Tag>
                    )}
                </Title>
                <Button onClick={onClose} size="small">
                    收起
                </Button>
            </div>

            {loading && (
                <div style={{ textAlign: "center", padding: "40px" }}>
                    <Spin size="large" />
                    <div style={{ marginTop: 16 }}>正在加载对比数据...</div>
                </div>
            )}

            {error && <Alert message="加载失败" description={error} type="error" showIcon style={{ marginBottom: 16 }} />}

            {comparison && !loading && !error && (
                <>
                    {/* 总体延误情况 */}
                    {(() => {
                        const overallDelay = calculateOverallDelay();
                        if (overallDelay) {
                            return <Alert message={overallDelay.description} type={overallDelay.type === "delayed" ? "warning" : "success"} showIcon style={{ marginBottom: 16 }} description={overallDelay.minutes >= 60 ? `约 ${Math.round(overallDelay.minutes / 60)} 小时，可能需要调整许可申请、餐食订单等业务安排` : "变更幅度较小，对业务影响有限"} />;
                        }
                        return null;
                    })()}

                    {/* 航班基本信息对比 */}
                    {renderFlightInfoComparison()}

                    {/* 变更详情 */}
                    {renderChanges()}
                </>
            )}
        </div>
    );
};

export default FlightComparisonViewer;
